import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import { User, Mail, Lock, Shield } from 'lucide-react';
import { toast } from 'sonner';

export default function ProfileDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast.success('Password updated');
      setNewPassword('');
    } catch (err) {
      toast.error(err.message || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1C1C1E] border-white/10 text-white max-w-md" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Profile</DialogTitle>
          <DialogDescription className="text-white/40">
            Your account information
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Avatar */}
          <div className="flex justify-center">
            <div className="w-20 h-20 rounded-full bg-[#2C2C2E] border-2 border-white/10 flex items-center justify-center">
              <User className="w-8 h-8 text-white/30" />
            </div>
          </div>

          {/* Name (read-only from ecosystem_users) */}
          <div className="space-y-1.5">
            <label className="tag-label text-white/40 block" style={{ fontSize: '10px', letterSpacing: '0.06em' }}>NAME</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                data-testid="profile-name-display"
                value={user?.name || ''}
                readOnly
                className="pl-10 bg-[#2C2C2E] border-white/10 text-white/60 rounded-lg cursor-default"
              />
            </div>
          </div>

          {/* Email (read-only from Supabase auth) */}
          <div className="space-y-1.5">
            <label className="tag-label text-white/40 block" style={{ fontSize: '10px', letterSpacing: '0.06em' }}>EMAIL</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                data-testid="profile-email-display"
                value={user?.email || ''}
                readOnly
                className="pl-10 bg-[#2C2C2E] border-white/10 text-white/60 rounded-lg cursor-default"
              />
            </div>
          </div>

          {/* Role (read-only) */}
          <div className="space-y-1.5">
            <label className="tag-label text-white/40 block" style={{ fontSize: '10px', letterSpacing: '0.06em' }}>ROLE</label>
            <div className="relative">
              <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                data-testid="profile-role-display"
                value={(user?.role || '—').toUpperCase()}
                readOnly
                className="pl-10 bg-[#2C2C2E] border-white/10 text-white/60 rounded-lg cursor-default"
              />
            </div>
          </div>

          {/* Separator */}
          <div className="border-t border-white/10 pt-4">
            <label className="tag-label text-white/40 block mb-1.5" style={{ fontSize: '10px', letterSpacing: '0.06em' }}>
              CHANGE PASSWORD
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                data-testid="profile-password-input"
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                className="pl-10 bg-[#2C2C2E] border-white/10 text-white placeholder:text-white/30 rounded-lg focus:ring-1 focus:ring-[#0A84FF]"
                placeholder="New password (min 6 chars)"
              />
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-white/60 hover:text-white hover:bg-white/5"
          >
            Close
          </Button>
          {newPassword && (
            <Button
              data-testid="profile-save-button"
              onClick={handleChangePassword}
              disabled={saving}
              className="bg-[#0A84FF] hover:opacity-85 text-white transition-opacity duration-150"
            >
              {saving ? 'Saving...' : 'Update Password'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
